import { _default } from "@max/index"
import Money from "./Money"
import { cache } from "@platform/cache"
import { storage } from "@platform/storage"
import { globalInfo } from "../../share/env/util"


let moneyAPI: Money
/**
 * @dtsModule max.lib
 * @dtsNamespace lib.money
 * @dtsDesc lib
 */
export const _money = {
  init({ moneyConfig, defaultCurrency }) {
    if (!moneyConfig)
      throw new Error(
        `Money init fail: ${defaultCurrency} ${JSON.stringify(moneyConfig)}`
      )
    let _currency = defaultCurrency
    if (globalInfo.get('appId')
      && __platform) {
      const currencyId = `__currency__${globalInfo.get('appId')}`
      const cacheCur = cache.get(currencyId) || storage.get(currencyId)
      cacheCur && (_currency = cacheCur as TCurrency)
    }
    moneyAPI = new Money(moneyConfig, _currency)
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  of(value: TMoneyValue, currency?: TCurrency, hmp?: boolean) {
    return moneyAPI?.of(value, currency, hmp)
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  ofMajor(amount: number, currency?: TCurrency, hmp?: boolean): TMoney {
    return moneyAPI?.ofMajor(amount, currency, hmp) as TMoney
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  isMoney(money: any, hmp?: boolean): boolean {
    return moneyAPI?.isMoney(money, hmp)
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  equals(money1: TMoney | string, money2: TMoney | string): boolean {
    return moneyAPI?.equals(money1, money2)
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  compare(money1: TMoney | string, money2: TMoney | string): 0 | 1 | -1 {
    return moneyAPI?.compare(money1, money2)
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  getContext(currency?: TCurrency): TMoneyConfig {
    let _currency = currency
    if (!_currency
      && globalInfo.get('appId')
      && __platform) {
      const currencyId = `__currency__${globalInfo.get('appId')}`
      const cacheCur = cache.get(currencyId) || storage.get(currencyId)
      cacheCur && (_currency = cacheCur as TCurrency)
    }
    return moneyAPI?.getContext(_currency)
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  setContext(currency: TCurrency): boolean {
    const flag = moneyAPI?.setContext(currency)
    if (flag
      && globalInfo.get('appId')
      && __platform) {
      const currencyId = `__currency__${globalInfo.get('appId')}`
      cache && cache.set(currencyId, currency)
      storage && storage.set(currencyId, currency)
    }
    return flag
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  format(money: TMoney | string, pattern?: string): string {
    return moneyAPI?.format(money, pattern)
  },
  // parse(str, pattern, currency) {
  //   return money?.parse(str, pattern, currency)
  // },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  toMajor(money: TMoney | string): number | TMoney | string {
    return moneyAPI?.toMajor(money)
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  symbol(money: TMoney | string): string {
    return moneyAPI?.symbol(money)
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  currency(money: TMoney | string): string {
    return moneyAPI?.currency(money)
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  locale(money: TMoney | string): string {
    return moneyAPI?.locale(money)
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  subtract(money1: TMoneyValue, money2: TMoneyValue, hmp?: boolean): TMoney {
    return moneyAPI?.subtract(money1, money2, hmp) as TMoney
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  multiply(money: TMoney, num: number, mode: "floor" | "round" | "ceil", hmp?: boolean): TMoney {
    return moneyAPI?.multiply(money, num, mode, hmp) as TMoney
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  divide(money: TMoney, num: number, mode: "floor" | "round" | "ceil", hmp?: boolean): TMoney {
    return moneyAPI?.divide(money, num, mode, hmp) as TMoney
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  add(money1: TMoneyValue, money2: TMoneyValue, hmp?: boolean): TMoney {
    return moneyAPI?.sum(money1, money2, hmp) as TMoney
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  avg(...moneys: TMoney[]): TMoney {
    return moneyAPI?.avg(...moneys) as TMoney
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  around(moneyObj: TMoney, config: {
    mode?: string
    offset?: number
    interval?: "unit" | "half_unit" | "quarter_unit"
    strategy?: "default" | "half_down" | "half_up"
  }) {
    return moneyAPI?.around(moneyObj, config)
  },
  // distribution(a, b) {
  //   return money?.distribution(a, b)
  // },
  // amount(value) {
  //   return money?.amount(value)
  // },
  // payAmount(value) {
  //   return money?.payAmount(value)
  // },
  // floor(amount, base) {
  //   return money?.floor(amount, base)
  // },
  // ceil(amount, base) {
  //   return money?.ceil(amount, base)
  // },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  min(money1: string | TMoney, money2: string | TMoney): string | TMoney {
    return moneyAPI?.min(money1, money2)
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  max(money1: string | TMoney, money2: string | TMoney): string | TMoney {
    return moneyAPI?.max(money1, money2)
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   */
  formatParts(money: TMoney | string, pattern?: string): { prefix: string; infix: string; suffix: string } {
    return moneyAPI?.formatParts(money, pattern)
  },
}
